import axios from 'axios';

// .trim() guards against a stray space in the env var (e.g. a trailing space in
// the Vercel REACT_APP_BACKEND_URL) producing an invalid URL like
// "https://api.host.com /api/..." that breaks every request.
const API_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from the LOGIN/RESET endpoints is the user mistyping — it must
    // render as the form's inline error, never trigger the session-expiry
    // redirect below. Before this guard, a wrong password hard-reloaded
    // /login mid-request: the browser cancelled every in-flight XHR, axios
    // surfaced "Network Error" instead of "Invalid email or password", and
    // the reload race could also swallow logouts/uploads (2026-07-22
    // incident, reproduced in-browser).
    const reqPath = String(error.config?.url || "");
    const isAuthAttempt = /\/auth\/(login|forgot-password|reset-password)/.test(reqPath);
    // Security audit 2026-09-01 §2.3: a Tier-3 endpoint that wants a fresh
    // authenticator check answers 403 {code:"mfa_step_up_required"} (or
    // "mfa_enrollment_required"). Raise it as a window event so the global
    // MfaStepUpModal can collect the code; the caller still sees the error.
    const mfaDetail = error.response?.data?.detail;
    if (error.response?.status === 403 && mfaDetail && typeof mfaDetail === 'object' &&
        (mfaDetail.code === 'mfa_step_up_required' || mfaDetail.code === 'mfa_enrollment_required')) {
      try { window.dispatchEvent(new CustomEvent('dealerflow:mfa-required', { detail: mfaDetail })); } catch { /* non-browser */ }
    }
    if (error.response?.status === 401 && !isAuthAttempt) {
      // If a support (impersonation) session's short-lived token just expired,
      // fall back to the stashed admin session instead of a full logout.
      const adminToken = localStorage.getItem('impersonator_token');
      if (adminToken) {
        const adminUser = localStorage.getItem('impersonator_user');
        localStorage.setItem('token', adminToken);
        if (adminUser) localStorage.setItem('user', adminUser);
        localStorage.removeItem('impersonator_token');
        localStorage.removeItem('impersonator_user');
        window.location.href = '/admin';
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Already on the login page → state is cleared, nothing to navigate to;
      // a hard reload here just races (and cancels) the user's own requests.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Download an authenticated endpoint as a browser file. Goes THROUGH the axios
// instance so the auth interceptor (and impersonation/401 fallback) applies —
// never rebuild the Authorization header by hand (arch-audit A8).
export async function downloadBlob(path, filename) {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// POST variant — for downloads that need a request body (e.g. a report export that
// carries a filter spec). Goes through the same axios instance, so the auth
// interceptor applies (never rebuild the Authorization header — arch-audit A8).
export async function postDownloadBlob(path, body, filename) {
  const res = await api.post(path, body || {}, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default api;